import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ExtensionOutlined,
  EmojiEventsOutlined,
  CheckCircleOutlined,
  TimerOutlined,
  GroupsOutlined,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import CrosswordGrid from "../components/CrosswordGrid";
import { loadDailyQuiz, recordQuizSolved } from "../api/publicApi";
import { kyivDateKey, plural } from "../utils/format";
import { tokens, radius, shadow } from "../../theme/theme";

const NICK_STORAGE = "leocity.crossword.nick";

function formatDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m === 0) return rest + " с";
  return m + " хв " + (rest < 10 ? "0" : "") + rest + " с";
}

function longDate(dateKey) {
  const parts = String(dateKey).split("-");
  if (parts.length !== 3) return dateKey;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  try {
    return d.toLocaleDateString("uk-UA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return dateKey;
  }
}

export default function QuizPage() {
  const [dateKey] = useState(() => kyivDateKey());
  const [quiz, setQuiz] = useState(undefined);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nickname, setNickname] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [alreadyFinished, setAlreadyFinished] = useState(false);

  useEffect(() => {
    let alive = true;
    loadDailyQuiz(dateKey)
      .then((data) => {
        if (alive) setQuiz(data);
      })
      .catch((err) => {
        console.error(err);
        if (alive) {
          setQuiz(null);
          setError(err);
        }
      });
    try {
      const stored = window.localStorage.getItem(NICK_STORAGE);
      if (stored) setNickname(stored);
    } catch (e) {
      /* storage disabled */
    }
    return () => {
      alive = false;
    };
  }, [dateKey]);

  const handleSolved = useCallback((info) => {
    setResult(info);
    // A hinted solve still deserves an acknowledgement, but it opens the
    // "no result recorded" variant of the dialog — there is nothing to submit.
    setDialogOpen(true);
  }, []);

  /**
   * Fired instead of onSolved when the grid was already complete before this
   * page load. No dialog: the player has seen it, and reopening it on every
   * refresh (with a 0-second time) was the bug this replaces.
   */
  const handleRestoredSolved = useCallback((info) => {
    setResult(info);
    setAlreadyFinished(true);
  }, []);

  const submitResult = async () => {
    setSaving(true);
    const name = (nickname || "").trim() || "Анонім";
    try {
      window.localStorage.setItem(NICK_STORAGE, name);
    } catch (e) {
      /* storage disabled */
    }
    const outcome = await recordQuizSolved(
      dateKey,
      name,
      result ? result.seconds : 0
    );
    setSaved(outcome);
    setSaving(false);
    setDialogOpen(false);
    if (outcome && outcome.item) {
      setQuiz((prev) =>
        prev
          ? Object.assign({}, prev, {
              solved: true,
              solvedBy: outcome.item.solvedBy || prev.solvedBy,
              solvedAt: outcome.item.solvedAt || prev.solvedAt,
              solveCount: outcome.item.solveCount || prev.solveCount,
            })
          : prev
      );
    }
  };

  /* ------------------------------------------------------------------ header */

  const header = (
    <Stack spacing={1.5} sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ExtensionOutlined sx={{ color: tokens.sand600 }} />
        <Typography variant="overline" sx={{ color: tokens.sand700 }}>
          Кросворд дня · {longDate(dateKey)}
        </Typography>
      </Stack>
      <Typography variant="h1" sx={{ fontSize: { xs: "2rem", md: "2.6rem" } }}>
        {quiz && quiz.title ? quiz.title : "Кросворд дня"}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
        Нова сітка щоранку. Клацніть клітинку й друкуйте — пробіл перемикає
        напрямок, стрілки рухають курсор. Прогрес зберігається у вашому браузері.
      </Typography>

      {quiz && (quiz.solved || (saved && saved.item)) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
          <Chip
            icon={<CheckCircleOutlined />}
            color="success"
            variant="outlined"
            label={
              "Розгадано" + (quiz.solvedBy ? " — перший: " + quiz.solvedBy : "")
            }
          />
          {quiz.solveCount > 0 && (
            <Chip
              icon={<GroupsOutlined />}
              variant="outlined"
              label={
                quiz.solveCount +
                " " +
                plural(quiz.solveCount, "гравець", "гравці", "гравців") +
                " впоралися"
              }
            />
          )}
        </Stack>
      )}

      {quiz && !quiz.solved && !saved && (
        <Chip
          icon={<EmojiEventsOutlined />}
          color="secondary"
          variant="outlined"
          label="Сьогодні його ще ніхто не розгадав"
          sx={{ alignSelf: "flex-start" }}
        />
      )}
    </Stack>
  );

  /* ------------------------------------------------------------------ states */

  if (quiz === undefined) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {header}
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: radius.lg }} />
      </Container>
    );
  }

  if (quiz === null) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {header}
        <Paper
          elevation={0}
          sx={{ p: { xs: 3, md: 5 }, borderRadius: radius.lg, textAlign: "center" }}
        >
          <Typography variant="h4" sx={{ mb: 1.5 }}>
            Сьогоднішній кросворд ще готується
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: "auto" }}>
            Нова сітка з'являється щоранку. Загляньте трохи пізніше — або поки що
            погортайте свіжі квартири в каталозі.
          </Typography>
          {error && (
            <Alert severity="warning" sx={{ mb: 3, textAlign: "left", borderRadius: radius.md }}>
              Не вдалося звернутися до бази кросвордів. Якщо це триває — перевірте
              таблицю <code>daily_quiz</code> та права доступу.
            </Alert>
          )}
          <Button component={RouterLink} to="/katalog" variant="contained" size="large">
            До каталогу квартир
          </Button>
        </Paper>
      </Container>
    );
  }

  /* ------------------------------------------------------------------ puzzle */

  // A hint spends the day's eligibility: the dialog then only acknowledges the
  // finish, and nothing is written to the database.
  const eligible = !!result && result.hints === 0;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      {header}

      <CrosswordGrid
        puzzle={quiz}
        storageKey={"leocity.crossword." + dateKey}
        onSolved={handleSolved}
        onRestoredSolved={handleRestoredSolved}
        solvedAlready={!!quiz.solved}
      />

      {saved && (
        <Alert
          severity="success"
          icon={<EmojiEventsOutlined />}
          sx={{ mt: 3, borderRadius: radius.md }}
        >
          {saved.first
            ? "Вітаємо — ви перший, хто розгадав сьогоднішній кросворд!"
            : "Кросворд розгадано. Результат зараховано."}
        </Alert>
      )}

      {alreadyFinished && !saved && (
        <Alert
          severity={result && result.hints > 0 ? "info" : "success"}
          icon={<CheckCircleOutlined />}
          sx={{ mt: 3, borderRadius: radius.md }}
        >
          {result && result.hints > 0
            ? "Цей кросворд ви вже дограли з підказками. Новий буде завтра."
            : "Цей кросворд ви вже розгадали. Новий буде завтра."}
        </Alert>
      )}

      {/* ------------------------------------------------------------- dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            px: 3,
            pt: 4,
            pb: 2,
            textAlign: "center",
            background:
              "linear-gradient(140deg, " + tokens.sand50 + ", " + tokens.blue50 + ")",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              backgroundColor: eligible ? tokens.sand500 : tokens.ink400,
              color: "#fff",
              boxShadow: shadow.md,
            }}
          >
            {eligible ? (
              <EmojiEventsOutlined sx={{ fontSize: 32 }} />
            ) : (
              <CheckCircleOutlined sx={{ fontSize: 32 }} />
            )}
          </Box>
          <Typography variant="h3">Кросворд розгадано!</Typography>
          {result && (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mt: 1.5, color: tokens.ink500 }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <TimerOutlined sx={{ fontSize: 17 }} />
                <Typography variant="body2">{formatDuration(result.seconds)}</Typography>
              </Stack>
              {result.hints > 0 && (
                <Typography variant="body2">
                  {result.hints}{" "}
                  {plural(result.hints, "підказка", "підказки", "підказок")}
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {eligible ? (
            <React.Fragment>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Залиште ім'я — воно з'явиться на сторінці кросворда, якщо ви
                сьогодні перший.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Ваше ім'я"
                placeholder="Наприклад, Олена"
                value={nickname}
                inputProps={{ maxLength: 24 }}
                onChange={(e) => setNickname(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !saving) submitResult();
                }}
              />
            </React.Fragment>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Сітку зібрано повністю — але цього разу з підказками, тож результат
              до сьогоднішніх переможців не потрапляє. Завтра о 4:00 з'явиться
              новий кросворд, і все почнеться з чистого аркуша.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {eligible ? (
            <React.Fragment>
              <Button onClick={() => setDialogOpen(false)} color="inherit">
                Пропустити
              </Button>
              <Button variant="contained" onClick={submitResult} disabled={saving}>
                {saving ? "Зберігаємо…" : "Зарахувати результат"}
              </Button>
            </React.Fragment>
          ) : (
            <Button variant="contained" onClick={() => setDialogOpen(false)}>
              Зрозуміло
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
